import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, IndianRupee, Loader2, Search } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const EMPTY = {
  name: "", category: "", size: "", mrp: 0, wholesale: 0, margin: 0, image: "", description: "",
};

const CATEGORIES = ["Laundry", "Personal Care", "Home Care", "Kitchen", "Other"];

export default function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // product being edited or null for new
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/products");
      setProducts(r.data);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, category: p.category, size: p.size, mrp: p.mrp,
      wholesale: p.wholesale, margin: p.margin, image: p.image, description: p.description,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.category || !form.size || !form.image) {
      toast.error("Please fill name, category, size, and image URL");
      return;
    }
    const payload = {
      ...form,
      mrp: Number(form.mrp) || 0,
      wholesale: Number(form.wholesale) || 0,
      margin: Number(form.margin) || 0,
    };
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/admin/products/${confirmDel.id}`);
      toast.success("Product deleted");
      setConfirmDel(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const filtered = products.filter((p) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return p.name.toLowerCase().includes(s) || p.category.toLowerCase().includes(s);
  });

  const autoMargin = Math.max(0, Number(form.mrp || 0) - Number(form.wholesale || 0));

  return (
    <div data-testid="products-panel">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="products-search"
            placeholder="Search product or category..."
            className="w-full h-11 pl-10 pr-4 rounded-md border border-[#E7E5E4] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C2410C]"
          />
        </div>
        <button
          onClick={openNew}
          data-testid="products-add-btn"
          className="pill-btn pill-btn-primary text-sm"
        >
          <Plus className="h-4 w-4" /> Add product
        </button>
      </div>

      {loading ? (
        <div className="p-16 text-center text-[#78716C]">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-[#78716C] bg-white border border-[#E7E5E4] rounded-md">
              No products match your search.
            </div>
          )}
          {filtered.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-product-${p.id}`}
              className="bg-white border border-[#E7E5E4] rounded-md overflow-hidden flex flex-col"
            >
              <div className="aspect-[4/3] bg-[#F5F5F4]">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C2410C]">
                  {p.category} · {p.size}
                </div>
                <h3 className="mt-1.5 font-display font-bold text-[#064E3B] leading-snug">{p.name}</h3>
                <p className="mt-1 text-xs text-[#57534E] line-clamp-2 flex-1">{p.description}</p>

                <div className="mt-3 flex items-center justify-between text-xs">
                  <div>
                    <div className="text-[10px] text-[#78716C] uppercase tracking-wider">Cost</div>
                    <div className="flex items-center font-bold text-[#064E3B]">
                      <IndianRupee className="h-3 w-3" />{p.wholesale}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#78716C] uppercase tracking-wider">MRP</div>
                    <div className="font-bold text-[#064E3B]">₹{p.mrp}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#78716C] uppercase tracking-wider">Profit</div>
                    <div className="font-bold text-[#C2410C]">₹{p.margin}</div>
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`product-edit-${p.id}`}
                    className="flex-1 h-9 rounded-md border border-[#064E3B] text-[#064E3B] text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-[#064E3B] hover:text-white transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => setConfirmDel(p)}
                    data-testid={`product-delete-${p.id}`}
                    className="h-9 w-9 rounded-md border border-[#E7E5E4] text-[#C2410C] flex items-center justify-center hover:bg-[#C2410C] hover:text-white hover:border-[#C2410C] transition-colors"
                    aria-label="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing ? "Edit product" : "Add new product"}
            </DialogTitle>
            <DialogDescription>
              {editing ? "Update the product details below." : "Fill in the product details to add it to the catalog."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <Field label="Name*">
              <input value={form.name} onChange={upd("name")} data-testid="pf-name" className={inputCls} placeholder="e.g. Liquid Detergent" />
            </Field>
            <Field label="Category*">
              <select value={form.category} onChange={upd("category")} data-testid="pf-category" className={inputCls}>
                <option value="">Select</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Size*">
              <input value={form.size} onChange={upd("size")} data-testid="pf-size" className={inputCls} placeholder="e.g. 1L, 500ml" />
            </Field>
            <Field label="Image URL*">
              <input value={form.image} onChange={upd("image")} data-testid="pf-image" className={inputCls} placeholder="https://..." />
            </Field>
            <Field label="MRP (₹)*">
              <input type="number" value={form.mrp} onChange={upd("mrp")} data-testid="pf-mrp" className={inputCls} inputMode="numeric" />
            </Field>
            <Field label="Your cost / Wholesale (₹)*">
              <input type="number" value={form.wholesale} onChange={upd("wholesale")} data-testid="pf-wholesale" className={inputCls} inputMode="numeric" />
            </Field>
            <Field label="Distributor margin (₹)*">
              <input type="number" value={form.margin} onChange={upd("margin")} data-testid="pf-margin" className={inputCls} inputMode="numeric" />
              <div className="text-[11px] text-[#78716C] mt-1">
                Auto suggested from MRP − Cost: <b className="text-[#064E3B]">₹{autoMargin}</b>
                {" "}
                <button
                  type="button"
                  onClick={() => setForm({ ...form, margin: autoMargin })}
                  className="ml-1 text-[#C2410C] font-bold hover:underline"
                >
                  Use this
                </button>
              </div>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Description*">
                <textarea rows={3} value={form.description} onChange={upd("description")} data-testid="pf-description" className={inputCls} placeholder="Short product description..." />
              </Field>
            </div>
            {form.image && (
              <div className="sm:col-span-2">
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#57534E] mb-2">Preview</div>
                <img src={form.image} alt="preview" className="h-40 rounded-md border border-[#E7E5E4] object-cover" />
              </div>
            )}
          </div>

          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="pill-btn pill-btn-ghost text-sm">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="product-save-btn" className="pill-btn pill-btn-primary text-sm disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editing ? "Save changes" : "Add product"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="product-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this product?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmDel?.name}" will be removed from the catalog. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="product-delete-confirm"
              onClick={doDelete}
              className="bg-[#C2410C] hover:bg-[#9A3412]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const inputCls = "w-full h-11 rounded-md border border-[#E7E5E4] bg-white px-3 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C] transition-colors";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#57534E]">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
